import { FC, memo, useEffect } from 'react';

import { FormInputsValue } from './_pages/booking/Form';
import { WideSelectOption } from './_pages/booking/Form/WideSelect';

export type PostMessageDataOutgoingType = {
	action: PostMessageOutgoingDataAction;
	data: {
		dates?: string[]; // on initialized - array of ISO 8601 string, ex. ["2023-11-07", "2023-11-08"];
		booking?: {
			dateTime: string;
			email: string | null;
			langCode: string; // ISO 639-1
			name: string;
			personCount: number;
			phone: string;
			comment?: string;
			visitDuration?: number;
			preferArea?: string;
		}; // on bookingCreated
		times?: number[]; // on initialized (for first date in dates[]) and dateUpdated - UNIX timestamp array, ex. [1703930400000, 1703931300000]
	};
};

export type PostMessageInstance = {
	source: MessageEventSource;
	origin: string;
	postMessage: (payload: PostMessageDataOutgoingType) => void;
};

type PostMessageListenerType = {
	dayOptions: WideSelectOption[];
	timeOptions: WideSelectOption[] | null;
	onFormChange: (value: string, name: string) => void;
	formState: FormInputsValue;
	postMessageInstance: PostMessageInstance | null;
	setPostMessageInstance: (instance: PostMessageInstance | null) => void;
};

export enum PostMessageIncomingDataAction {
	updateTime = 'updateTime',
	updateDate = 'updateDate',
	initialize = 'initialize',
}

export enum PostMessageOutgoingDataAction {
	initialized = 'initialized',
	dateUpdated = 'dateUpdated',
	bookingCreated = 'bookingCreated',
}

export type PostMessageIncomingDataType = {
	action: PostMessageIncomingDataAction;
	date?: string; // updateDate - as ISO 8601
	time?: number; // updateTime - as UNIX timestamp
};

const PostMessageListener: FC<PostMessageListenerType> = ({
	timeOptions,
	dayOptions,
	onFormChange,
	formState,
	postMessageInstance,
	setPostMessageInstance,
}) => {
	const { source, origin } = postMessageInstance ?? {};

	const bindEventPostMessage = (event: MessageEvent) => (payload: PostMessageDataOutgoingType) =>
		(event.source as WindowProxy).postMessage(payload, event.origin);

	const listenerHandler = (event: MessageEvent) => {
		console.log(`[listenerHandler] New event: ${event.data?.action || 'Unknown action'}`);
		const data = event.data as PostMessageIncomingDataType;
		try {
			if (data?.action === PostMessageIncomingDataAction.initialize) {
				const boundPostMessage = bindEventPostMessage(event);
				setPostMessageInstance({
					origin: event.origin,
					source: event.source!,
					postMessage: boundPostMessage,
				});
				boundPostMessage({
					action: PostMessageOutgoingDataAction.initialized,
					data: {
						dates: dayOptions.map((d) => d.value),
						times: timeOptions?.map((t) => t.value),
					},
				} as PostMessageDataOutgoingType);
			}
			if (data?.action === PostMessageIncomingDataAction.updateDate) {
				if (data.date) onFormChange(data.date, 'date');
			}
			if (data?.action === PostMessageIncomingDataAction.updateTime) {
				if (data.time) onFormChange(data.time as any, 'time');
			}
		} catch (e) {
			console.error(e);
		}
	};

	const handleSendDateUpdate = () => {
		if (!source || !origin) return;

		source?.postMessage({
			action: PostMessageOutgoingDataAction.dateUpdated,
			data: {
				dates: dayOptions.map((d) => d.value),
				times: timeOptions?.map((t) => t.value),
			},
		} as PostMessageDataOutgoingType);
	};

	useEffect(() => {
		if (formState?.date) {
			handleSendDateUpdate();
		}
	}, [formState?.date]);

	useEffect(() => {
		window.addEventListener('message', listenerHandler, false);

		return () => {
			window.removeEventListener('message', listenerHandler);
		};
	}, []);

	return null;
};

export default memo(PostMessageListener);
